import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserProfile, createDefaultUserProfile } from '../utils/useUserProfile';
import '../App.css';
import './BasicFitting.css';

type BasicStep =
  | {
      title: string;
      question: string;
      field: 'gender' | 'handedness';
      type: 'select';
      options: string[];
    }
  | {
      title: string;
      question: string;
      field: 'seven_iron';
      type: 'club';
      options: {
        ballFlight: string[];
      };
    }
  | {
      title: string;
      question: string;
      field: 'seven_iron_miss_tendency';
      type: 'club';
      options: {
        missTendency: string[];
      };
    }
  | {
      title: string;
      question: string;
      field: 'driver';
      type: 'club';
      options: {
        ballFlight: string[];
      };
    }
  | {
      title: string;
      question: string;
      field: 'driver_downswing_transition';
      type: 'club';
      options: {
        downswingTransition: string[];
      };
    };

const ballFlightImages: Record<string, string> = {
  Low: '/BallFlightLow.jpg',
  Mid: '/BallFlightMedium.jpg',
  High: '/BallFlightHigh.jpg'
};

const missTendencyImages: Record<string, string> = {
  Hook: '/MissTendencyHook.jpg',
  Draw: '/MissTendencyDraw.jpg',
  Straight: '/MissTendencyStraight.jpg',
  Fade: '/MissTendencyFade.jpg',
  Slice: '/MissTendencySlice.jpg'
};

const transitionImages: Record<string, string> = {
  Smooth: '/TransitionSmooth.gif',
  Neutral: '/TransitionNeutral.gif',
  Abrupt: '/TransitionAbrupt.gif'
};

const BasicFitting: React.FC = () => {
  const navigate = useNavigate();
  const { createProfile } = useUserProfile();
  
  // Basic profile state
  const [profileData, setProfileData] = useState({
    gender: 'Male',
    handedness: 'Right',
    seven_iron_carry_distance: '130',
    seven_iron_ball_flight: '',
    seven_iron_miss_tendency: '',
    driver_carry_distance: '220',
    driver_loft: '10.5',
    driver_ball_flight: '',
    driver_downswing_transition: ''
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhyThisMatters, setShowWhyThisMatters] = useState(false);

  const steps: BasicStep[] = [
    {
      title: 'Gender',
      question: 'What is your gender?',
      field: 'gender',
      type: 'select',
      options: ['Male', 'Female']
    },
    {
      title: 'Handedness',
      question: 'Which is your dominant hand?',
      field: 'handedness',
      type: 'select',
      options: ['Right', 'Left']
    },
    {
      title: '7-Iron',
      question: 'Tell us about your 7-Iron',
      field: 'seven_iron',
      type: 'club',
      options: {
        ballFlight: ['Low', 'Mid', 'High']
      }
    },
    {
      title: '7-Iron Miss Tendency',
      question: 'What is your 7-Iron Shot Shape?',
      field: 'seven_iron_miss_tendency',
      type: 'club',
      options: {
        missTendency: ['Hook', 'Draw', 'Fade', 'Slice']
      }
    },
    {
      title: 'Driver',
      question: 'Tell us about your driver',
      field: 'driver',
      type: 'club',
      options: {
        ballFlight: ['Low', 'Mid', 'High']
      }
    },
    {
      title: 'Driver Transition',
      question: 'What is your driver downswing transition?',
      field: 'driver_downswing_transition',
      type: 'club',
      options: {
        downswingTransition: ['Smooth', 'Neutral', 'Abrupt']
      }
    }
  ];

  const handleInputChange = (field: string, value: string) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleSelectionNext = (nextStateOverride: Partial<typeof profileData>) => {
    const nextProfileData = { ...profileData, ...nextStateOverride };
    setProfileData(nextProfileData);

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit(nextProfileData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const determineSkillLevel = (finalProfileData: typeof profileData) => {
    const gender = (finalProfileData.gender || '').toLowerCase();
    const isMale = gender === 'male';
    const sevenIronCarry = Number(finalProfileData.seven_iron_carry_distance) || 0;
    const driverCarry = Number(finalProfileData.driver_carry_distance) || 0;
    const driverTransition = (finalProfileData.driver_downswing_transition || '').toLowerCase();
    const ironFlight = (finalProfileData.seven_iron_ball_flight || '').toLowerCase();
    const driverFlight = (finalProfileData.driver_ball_flight || '').toLowerCase();
    const ironMiss = (finalProfileData.seven_iron_miss_tendency || '').toLowerCase();

    const thresholds = isMale
      ? {
          beginnerSevenIron: 120,
          beginnerDriver: 180,
          advancedSevenIron: 175,
          advancedDriver: 290
        }
      : {
          beginnerSevenIron: 100,
          beginnerDriver: 160,
          advancedSevenIron: 160,
          advancedDriver: 260
        };

    const beginnerSignals = [
      sevenIronCarry <= thresholds.beginnerSevenIron,
      driverCarry <= thresholds.beginnerDriver,
      ['slice', 'hook'].includes(ironMiss),
      ['high'].includes(ironFlight),
      ['high'].includes(driverFlight),
      ['abrupt'].includes(driverTransition)
    ].filter(Boolean).length;

    const advancedSignals = [
      sevenIronCarry >= thresholds.advancedSevenIron,
      driverCarry >= thresholds.advancedDriver,
      ['smooth', 'neutral'].includes(driverTransition),
      ['low', 'mid'].includes(ironFlight),
      ['low', 'mid'].includes(driverFlight),
      ['straight', 'draw', 'fade'].includes(ironMiss)
    ].filter(Boolean).length;

    if (beginnerSignals >= 3) {
      return 'beginner';
    }

    if (advancedSignals >= 6) {
      return 'advanced';
    }

    return 'intermediate';
  };

  const handleSubmit = async (finalProfileData = profileData) => {
    setIsSubmitting(true);
    try {
      const defaultProfile: any = createDefaultUserProfile();
      const skillLevel = determineSkillLevel(finalProfileData);
      defaultProfile.general = {
        ...defaultProfile.general,
        gender: finalProfileData.gender,
        handedness: finalProfileData.handedness,
        skill_level: skillLevel
      };

      const sevenIronDistance = finalProfileData.seven_iron_carry_distance
        ? Number(finalProfileData.seven_iron_carry_distance)
        : null;
      const driverDistance = finalProfileData.driver_carry_distance
        ? Number(finalProfileData.driver_carry_distance)
        : null;
      const pitchingWedgeDistance = sevenIronDistance !== null
        ? Math.max(0, sevenIronDistance - 30)
        : null;

      defaultProfile['7-Iron'] = {
        model: null,
        shaft: null,
        distance: sevenIronDistance,
        notes: null,
        miss_tendency: finalProfileData.seven_iron_miss_tendency || null,
        ball_flight: finalProfileData.seven_iron_ball_flight || null,
        loft: null
      };

      defaultProfile['Pitching Wedge'] = {
        model: null,
        shaft: null,
        distance: pitchingWedgeDistance,
        notes: null,
        miss_tendency: finalProfileData.seven_iron_miss_tendency || null,
        ball_flight: finalProfileData.seven_iron_ball_flight || null,
        loft: null
      };

      defaultProfile.Driver = {
        model: null,
        shaft: null,
        distance: driverDistance,
        notes: null,
        miss_tendency: null,
        ball_flight: finalProfileData.driver_ball_flight || null,
        loft: finalProfileData.driver_loft ? Number(finalProfileData.driver_loft) : 10.5,
        downswing_transition: finalProfileData.driver_downswing_transition || null
      };

      defaultProfile.driverDistance = driverDistance;
      defaultProfile.sevenIronDistance = sevenIronDistance;
      defaultProfile.pitchingWedgeDistance = pitchingWedgeDistance;

      await createProfile(defaultProfile);
      navigate('/club-fitting');
    } catch (error) {
      console.error('Error creating profile:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const canProceed = true;
  const contextualHelper =
    'Pick the option that best matches your game. You can always update this later.';
  const whyThisMatters =
    (currentStepData as any).why ||
    'We use this to personalize your fitting recommendations and suggested specs. You can update this anytime.';

  useEffect(() => {
    setShowWhyThisMatters(false);
  }, [currentStep]);

  return (
    <div className="nf-home fitting-container fitting-container--basic">
      <div className="fitting-card">
        {/* Progress Bar */}
        <div className="fitting-progress">
          <div className="fitting-progress-header">
            <div className="fitting-progress-left">
              <button
                type="button"
                className="fitting-back-link"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <svg viewBox="0 0 24 24" className="fitting-back-icon" aria-hidden="true">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
                <span className="fitting-back-label">Back</span>
              </button>
            </div>
            <span className="fitting-progress-step">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <div className="fitting-progress-bar">
            <div
              className="fitting-progress-fill"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="fitting-question">
          <p className="fitting-question-eyebrow">Tell us a little about yourself:</p>
          <h3 className="fitting-question-title">{currentStepData.question}</h3>
          <div className="fitting-context-pill-wrapper">
            <button
              type="button"
              className="fitting-context-pill-button"
              onClick={() => setShowWhyThisMatters((prev) => !prev)}
              aria-expanded={showWhyThisMatters}
              aria-controls="fitting-why-this-matters"
            >
              Why this matters <span className="fitting-context-pill-icon">i</span>
            </button>
            {showWhyThisMatters && (
              <div
                id="fitting-why-this-matters"
                role="status"
                className="fitting-context-popover"
              >
                {whyThisMatters}
              </div>
            )}
          </div>
          <p className="fitting-help">{contextualHelper}</p>
          <p>&nbsp;</p>
          {/* Input based on type */}
          {currentStepData.type === 'select' && (
            <div className="fitting-options">
              {currentStepData.options?.map((option) => (
                <button
                  type="button"
                  key={option}
                  onClick={() => {
                    handleSelectionNext({ [currentStepData.field]: option } as any);
                  }}
                  className={`fitting-option ${
                    profileData[currentStepData.field as keyof typeof profileData] === option
                      ? 'selected'
                      : ''
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'seven_iron' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#ffffff', fontSize: '1.55rem', flex: '0 0 auto' }}>7-Iron Carry (Yards)</label>
                  <span style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.4rem' }}>{profileData.seven_iron_carry_distance || 130} yds</span>
                </div>
                <input
                  type="range"
                  min={50}
                  max={200}
                  step={1}
                  value={profileData.seven_iron_carry_distance || '130'}
                  onChange={(e) => {
                    const nextValue = Number(e.target.value);
                    const boundedValue = Number.isNaN(nextValue)
                      ? '130'
                      : String(Math.min(200, Math.max(50, nextValue)));
                    handleInputChange('seven_iron_carry_distance', boundedValue);
                  }}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Ball Flight</label>
                <div className="fitting-choice-row ball-flight-row">
                  {currentStepData.options.ballFlight.map((option: string) => {
                    const normalizedOption = option.toLowerCase();
                    const isSelected = (profileData.seven_iron_ball_flight || '').toLowerCase() === normalizedOption;

                    return (
                      <button
                        type="button"
                        key={option}
                        onClick={() => {
                          handleSelectionNext({ seven_iron_ball_flight: normalizedOption } as any);
                        }}
                        className={`fitting-option fitting-choice-option ${isSelected ? 'selected' : ''}`}
                      >
                        <img
                          src={ballFlightImages[option]}
                          alt={`${option} ball flight`}
                          style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                        />
                        <span>{option}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'seven_iron_miss_tendency' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1.25rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Miss Tendency</label>
                <div className="fitting-choice-row">
                  {currentStepData.options.missTendency.map((option: string) => (
                    <button
                      type="button"
                      key={option}
                      onClick={() => {
                        handleSelectionNext({ seven_iron_miss_tendency: option } as any);
                      }}
                      className={`fitting-option fitting-choice-option ${profileData.seven_iron_miss_tendency === option ? 'selected' : ''}`}
                    >
                      <img
                        src={missTendencyImages[option]}
                        alt={`${option} miss tendency`}
                        style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                      <span>{option}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'driver' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1.25rem' }}>
              <div style={{ display: 'grid', gap: '0.75rem', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#ffffff', fontSize: '1.55rem', flex: '0 0 auto' }}>Driver Carry (Yards)</label>
                  <span style={{ fontWeight: 700, color: '#ffffff', fontSize: '1.4rem' }}>{profileData.driver_carry_distance || 240} yds</span>
                </div>
                <input
                  type="range"
                  min={100}
                  max={350}
                  step={1}
                  value={profileData.driver_carry_distance || '240'}
                  onChange={(e) => {
                    const nextValue = Number(e.target.value);
                    const boundedValue = Number.isNaN(nextValue)
                      ? '240'
                      : String(Math.min(350, Math.max(100, nextValue)));
                    handleInputChange('driver_carry_distance', boundedValue);
                  }}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Ball Flight</label>
                <div className="fitting-choice-row ball-flight-row">
                  {currentStepData.options.ballFlight.map((option: string) => {
                    const normalizedOption = option.toLowerCase();
                    const isSelected = (profileData.driver_ball_flight || '').toLowerCase() === normalizedOption;

                    return (
                      <button
                        type="button"
                        key={option}
                        onClick={() => {
                          handleSelectionNext({ driver_ball_flight: normalizedOption } as any);
                        }}
                        className={`fitting-option fitting-choice-option ${isSelected ? 'selected' : ''}`}
                      >
                        <img
                          src={ballFlightImages[option]}
                          alt={`${option} ball flight`}
                          style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                        />
                        <span>{option}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {currentStepData.type === 'club' && currentStepData.field === 'driver_downswing_transition' && (
            <div className="fitting-options" style={{ display: 'grid', gap: '1.25rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap', width: '100%' }}>
                <label style={{ fontWeight: 700, whiteSpace: 'nowrap', color: '#ffffff', fontSize: '1.55rem', flex: '0 0 auto' }}>Driver Loft (Deg)</label>
                <div className="fitting-choice-row driver-loft-row" style={{ flexWrap: 'wrap' }}>
                  {['8.5', '9.5', '10.5', '11.5', '12.5'].map((option) => (
                    <button
                      type="button"
                      key={option}
                      onClick={() => {
                        setProfileData((prev) => ({ ...prev, driver_loft: option }));
                      }}
                      className={`fitting-option fitting-choice-option driver-loft-option ${profileData.driver_loft === option ? 'selected' : ''}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 700, color: '#ffffff', fontSize: '1.55rem' }}>Downswing Transition</label>
                <div className="fitting-choice-row downswing-transition-row">
                  {currentStepData.options.downswingTransition.map((option: string) => (
                    <button
                      type="button"
                      key={option}
                      onClick={() => {
                        handleSelectionNext({ driver_downswing_transition: option } as any);
                      }}
                      className={`fitting-option fitting-choice-option ${profileData.driver_downswing_transition === option ? 'selected' : ''}`}
                    >
                      <img
                        src={transitionImages[option]}
                        alt={`${option} downswing transition`}
                        style={{ width: '100%', maxWidth: '120px', borderRadius: '8px', objectFit: 'cover' }}
                      />
                      <span>{option}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {false && (
            <input
              type="text"
              value=""
              onChange={() => undefined}
              className="fitting-input"
            />
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="fitting-nav" style={{ marginTop: '4rem' }}>
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="fitting-btn fitting-btn--ghost"
          >
            Back
          </button>

          <button
            onClick={handleNext}
            disabled={!canProceed || isSubmitting}
            className="fitting-btn fitting-btn--primary"
          >
            {isSubmitting ? 'Creating Profile...' : isLastStep ? 'Complete' : 'Next'}
          </button>
        </div>

        {/* Skip link for all questions */}
        <div className="fitting-skip-wrapper">
          <button onClick={handleNext} className="fitting-skip">
            Skip this question
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicFitting;
